import { Bot, User, Wrench, Loader2, Trash2, X } from "lucide-react";

interface ChatMessage {
	id: string;
	role: string;
	parts: Array<{ type: string; text?: string; [key: string]: any }>;
}

interface ChatPanelProps {
	messages: ChatMessage[];
	isStreaming: boolean;
	onClearHistory: () => void;
	onClose: () => void;
	onDeleteMessage: (id: string) => void;
	messagesEndRef: React.RefObject<HTMLDivElement | null>;
}

function renderToolPart(part: any, i: number) {
	const toolInvocation = part.toolInvocation || part;
	const toolName =
		toolInvocation.toolName ||
		(typeof part.type === "string" && part.type.startsWith("tool-") ? part.type.slice(5) : "unknown");
	const state = (toolInvocation.state || part.state || "") as string;
	const isLoading = [
		"input-streaming", "input-available", "call", "partial-call",
		"approval-requested", "approval-responded",
	].includes(state);
	const isDone = state === "output-available" || state === "result";
	const isError = state === "output-error" || state === "output-denied";

	return (
		<div key={i} className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-slate-700/30 border border-slate-700/50 text-xs text-slate-400">
			<Wrench className="w-3 h-3 shrink-0" />
			<span className="font-mono truncate">{toolName}</span>
			{isLoading && <Loader2 className="w-3 h-3 animate-spin ml-auto shrink-0" />}
			{isDone && <span className="ml-auto text-emerald-400 shrink-0">Done</span>}
			{isError && <span className="ml-auto text-red-400 shrink-0">Error</span>}
		</div>
	);
}

export default function ChatPanel({
	messages,
	isStreaming,
	onClearHistory,
	onClose,
	onDeleteMessage,
	messagesEndRef,
}: ChatPanelProps) {
	return (
		<div className="absolute bottom-20 left-1/2 -translate-x-1/2 z-40 w-full max-w-2xl px-4">
			<div className="bg-slate-800/95 backdrop-blur-xl border border-slate-700/50 rounded-2xl shadow-2xl flex flex-col max-h-[60vh]">
				{/* Header */}
				<div className="flex items-center justify-between px-4 py-3 border-b border-slate-700/50">
					<div className="flex items-center gap-2">
						<Bot className="w-4 h-4 text-blue-400" />
						<span className="text-sm font-medium text-white">Chat</span>
						{isStreaming && <span className="text-xs text-blue-400 animate-pulse">thinking...</span>}
					</div>
					<div className="flex items-center gap-1">
						<button
							onClick={onClearHistory}
							disabled={isStreaming || messages.length === 0}
							className="p-1.5 text-slate-400 hover:text-red-400 rounded-lg hover:bg-red-400/10 transition-all disabled:opacity-30 disabled:hover:text-slate-400 disabled:hover:bg-transparent"
							title="Clear chat"
						>
							<Trash2 className="w-3.5 h-3.5" />
						</button>
						<button onClick={onClose} className="p-1.5 text-slate-400 hover:text-white rounded-lg hover:bg-slate-700 transition-all" title="Close chat">
							<X className="w-4 h-4" />
						</button>
					</div>
				</div>
				{/* Messages */}
				<div className="flex-1 overflow-y-auto px-4 py-3 space-y-3">
					{messages.length === 0 ? (
						<div className="flex flex-col items-center justify-center py-8 text-center">
							<Bot className="w-10 h-10 text-slate-600 mb-2" />
							<p className="text-slate-500 text-sm">Send a message to start</p>
						</div>
					) : (
						messages.map((msg) => (
							<div key={msg.id} className={`group flex gap-2.5 ${msg.role === "user" ? "justify-end" : "justify-start"}`}>
								{msg.role !== "user" && (
									<div className="shrink-0 w-7 h-7 rounded-lg bg-blue-500/20 flex items-center justify-center mt-0.5">
										<Bot className="w-3.5 h-3.5 text-blue-400" />
									</div>
								)}
								<div className="max-w-[80%] space-y-1.5 relative">
									{msg.parts.map((part, i) => {
										if (part.type === "text") {
											if (!part.text?.trim()) return null;
											return (
												<div
													key={i}
													className={`px-3 py-2 rounded-xl text-sm leading-relaxed whitespace-pre-wrap ${
														msg.role === "user"
															? "bg-blue-600 text-white"
															: "bg-slate-700/60 text-slate-200"
													}`}
												>
													{part.text}
												</div>
											);
										}
										if (
											part.type === "tool-invocation" ||
											(typeof part.type === "string" && part.type.startsWith("tool-")) ||
											part.type === "dynamic-tool"
										) {
											return renderToolPart(part, i);
										}
										// Ignore unknown part types (e.g. source, reasoning, step-start)
										return null;
									})}
									<button
										onClick={() => onDeleteMessage(msg.id)}
										className="absolute -right-6 top-0 p-1 text-slate-600 hover:text-red-400 rounded opacity-0 group-hover:opacity-100 transition-opacity"
										title="Delete message"
									>
										<X className="w-3 h-3" />
									</button>
								</div>
								{msg.role === "user" && (
									<div className="shrink-0 w-7 h-7 rounded-lg bg-slate-600/50 flex items-center justify-center mt-0.5">
										<User className="w-3.5 h-3.5 text-slate-300" />
									</div>
								)}
							</div>
						))
					)}
					{isStreaming && messages.length > 0 && messages[messages.length - 1]?.role === "user" && (
						<div className="flex gap-2.5">
							<div className="shrink-0 w-7 h-7 rounded-lg bg-blue-500/20 flex items-center justify-center">
								<Bot className="w-3.5 h-3.5 text-blue-400" />
							</div>
							<div className="px-3 py-2 rounded-xl bg-slate-700/60">
								<Loader2 className="w-4 h-4 text-slate-400 animate-spin" />
							</div>
						</div>
					)}
					<div ref={messagesEndRef} />
				</div>
			</div>
		</div>
	);
}
